import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';

// =========================================
// Types
// =========================================

interface ExtractionField {
  name: string;
  type: 'text' | 'number' | 'boolean' | 'list' | 'date';
  description: string;
  required: boolean;
}

interface Extraction {
  id: string;
  user_id: string;
  source_id: string;
  fields: Record<string, unknown>;
  extraction_schema: ExtractionField[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
}

interface ExtractionWithSource extends Extraction {
  sources: {
    id: string;
    title: string | null;
    file_name: string;
  };
}

interface RunExtractionInput {
  source_id: string;
  schema: ExtractionField[];
  ai_model?: 'gpt-4-turbo' | 'claude-3.5-sonnet';
}

interface BatchExtractionInput {
  source_ids: string[];
  schema: ExtractionField[];
  ai_model?: 'gpt-4-turbo' | 'claude-3.5-sonnet';
}

const EXTRACTIONS_KEY = 'extractions';

// =========================================
// Queries
// =========================================

/** Fetch all extractions for the current user */
export function useExtractions(sourceId?: string) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [EXTRACTIONS_KEY, user?.id, sourceId],
    queryFn: async (): Promise<ExtractionWithSource[]> => {
      let query = supabase
        .from('extractions')
        .select('*, sources(id, title, file_name)')
        .order('created_at', { ascending: false });

      if (sourceId) {
        query = query.eq('source_id', sourceId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as ExtractionWithSource[];
    },
    enabled: !!user,
  });
}

/** Fetch a single extraction */
export function useExtraction(id: string | undefined) {
  return useQuery({
    queryKey: [EXTRACTIONS_KEY, id],
    queryFn: async (): Promise<ExtractionWithSource> => {
      const { data, error } = await supabase
        .from('extractions')
        .select('*, sources(id, title, file_name)')
        .eq('id', id!)
        .single();

      if (error) throw error;
      return data as unknown as ExtractionWithSource;
    },
    enabled: !!id,
  });
}

// =========================================
// Mutations
// =========================================

/** Run structured data extraction on a single source */
export function useRunExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RunExtractionInput) => {
      return await api.extractData(input) as Extraction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EXTRACTIONS_KEY] });
      toast.success('Data extracted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Extraction failed: ${error.message}`);
    },
  });
}

/** Run batch extraction on multiple sources */
export function useRunBatchExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: BatchExtractionInput) => {
      const results: Extraction[] = [];
      const errors: Array<{ source_id: string; error: string }> = [];

      for (const source_id of input.source_ids) {
        try {
          const data = await api.extractData({
            source_id,
            schema: input.schema,
            ai_model: input.ai_model,
          });
          results.push(data as Extraction);
        } catch (err) {
          errors.push({
            source_id,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }

      return { results, errors };
    },
    onSuccess: ({ results, errors }) => {
      queryClient.invalidateQueries({ queryKey: [EXTRACTIONS_KEY] });
      if (errors.length === 0) {
        toast.success(`Extracted data from ${results.length} sources`);
      } else {
        toast.warning(
          `Extracted ${results.length} sources, ${errors.length} failed`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(`Batch extraction failed: ${error.message}`);
    },
  });
}

/** Update extracted fields (manual edits) */
export function useUpdateExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      fields,
    }: {
      id: string;
      fields: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase
        .from('extractions')
        .update({ fields })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Extraction;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [EXTRACTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [EXTRACTIONS_KEY, data.id] });
      toast.success('Extraction updated');
    },
    onError: (error: Error) => {
      toast.error(`Update failed: ${error.message}`);
    },
  });
}

/** Delete an extraction */
export function useDeleteExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('extractions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EXTRACTIONS_KEY] });
      toast.success('Extraction deleted');
    },
    onError: (error: Error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });
}

// =========================================
// Export utilities
// =========================================

/** Convert extractions to CSV string */
export function extractionsToCSV(
  extractions: ExtractionWithSource[],
  schema: ExtractionField[]
): string {
  const headers = ['Source', ...schema.map((f) => f.name)];
  const rows = extractions.map((ext) => {
    const sourceName = ext.sources?.title || ext.sources?.file_name || 'Unknown';
    const values = schema.map((f) => {
      const val = ext.fields[f.name];
      if (val === null || val === undefined) return '';
      if (Array.isArray(val)) return val.join('; ');
      return String(val);
    });
    return [sourceName, ...values];
  });

  const csvRows = [headers, ...rows].map((row) =>
    row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  );

  return csvRows.join('\n');
}

/** Download CSV */
export function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export type {
  Extraction,
  ExtractionWithSource,
  ExtractionField,
  RunExtractionInput,
  BatchExtractionInput,
};
